import { z } from "zod";

// Static envs (managed in `.github/frontend-envs.json` `envs.<env>.frontend`)
// plus the feature-env shape `ft-<numeric-ticket>` that the FeatureEnvStack
// writes at deploy time. Pattern matches the validation in
// `.github/workflows/feature-env-lifecycle.yml` (`^ft-[0-9]+$`); examples:
// `ft-12358`. Consumers that only care about `local` continue to work
// (feature-envs are not local).
const StaticDeployEnvSchema = z.enum([
  "local",
  "dev",
  "stg",
  "partner",
  "prod",
]);
// Brand the feature-env regex so `z.infer<typeof DeployEnvSchema>` stays a
// discriminating union instead of collapsing to plain `string`. Downstream
// callers can still do `deployEnv === "local"` (literal narrows out the
// branded branch); a value like `ft-12358` is typed as
// `string & z.BRAND<"FeatureEnvName">` so it cannot be confused with an
// arbitrary string literal at compile time.
const FeatureEnvNameSchema = z
  .string()
  .regex(/^ft-[0-9]+$/)
  .brand<"FeatureEnvName">();

const DeployEnvSchema = z.union([StaticDeployEnvSchema, FeatureEnvNameSchema]);

export type DeployEnv = z.infer<typeof DeployEnvSchema>;

export const RuntimeConfigSchema = z
  .object({
    VITE_DEPLOY_ENV: DeployEnvSchema,
    VITE_GRAPHQL_URL: z.string().url(),
    VITE_GRAPHQL_LAMBDA_URL: z.string().url(),
    VITE_KEYCLOAK_CLIENT: z.string().min(1),
    VITE_THREED_PLANNER_URL: z.string().url(),
    VITE_ICAL_GENERATOR_ENDPOINT: z.string().url(),
    VITE_EMBEDDABLE_WIZARD_URL: z.string().url(),
    // Public client-exposed by design; protected by GCP HTTP referrer
    // restriction on the API key. See MEIS-11514 for the operational
    // tracking ticket of an edge case (PDF generator has no Referer).
    VITE_GOOGLE_MAPS_API_KEY: z.string().min(1),
    // Mobile-only (Capacitor); web bundles see this as undefined.
    VITE_CAPACITOR_GRAPHQL_URL: z.string().url().optional(),
    // Per-build identity, written by the deploy stage of the CodePipeline
    // (or by `write-mobile-config.ts` for Capacitor mobile builds) to the
    // commit SHA of the deployed Frontend image. Optional during the
    // MEIS-12358 Phase 8 rollout — old infra deploy stages don't yet
    // write the field; callers read it through `getVersion()` in
    // `environment.ts`, which returns `undefined` when absent and lets
    // UI surfaces fall back to "unknown" (the legacy build-time
    // `VERSION` const was removed in Phase 8). Once every env's deploy
    // stage writes `version`, this can be tightened to required.
    version: z.string().min(1).optional(),
  })
  .strict();

export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

let runtimeConfig: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  // Use default mode + credentials (same-origin, credentials=same-origin)
  // so the browser sends auth cookies the way it does for index.html
  // itself — auth-protected deployments would otherwise 401 on this
  // request. Must match the `<link rel="preload" href="/config.json"
  // as="fetch">` in the entrypoint HTML (no `crossorigin` attribute,
  // for the same reason) so the preloaded response is reused.
  const response = await fetch("/config.json", { cache: "no-store" });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch /config.json: ${response.status} ${response.statusText}`
    );
  }
  const payload: unknown = await response.json();
  runtimeConfig = RuntimeConfigSchema.parse(payload);
  return runtimeConfig;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!runtimeConfig) {
    throw new Error(
      "Runtime config not loaded. loadRuntimeConfig() must complete before getRuntimeConfig() is called. " +
        "If this fires inside a test, call setRuntimeConfigForTesting() in setup."
    );
  }
  return runtimeConfig;
}

export function setRuntimeConfigForTesting(config: RuntimeConfig): void {
  runtimeConfig = config;
}

export function clearRuntimeConfigForTesting(): void {
  runtimeConfig = undefined;
}
